import { useState } from "react";
import { UserPlus, History, ShieldAlert, ShieldCheck, XCircle, Users, Wallet, HandCoins, UserX, Timer } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApp } from "@/context/AppContext";
import { fmtDate, vals } from "@/lib/format";
import { syncErrText } from "@/lib/syncErrors";
import { BigButton, PageTitle } from "@/components/ui-kit";
import StaffProfile from "@/components/team/StaffProfile";
import PermissionsDialog from "@/components/team/PermissionsDialog";
import { staffEvents, staffDebt, monthSummary, onLeave, today } from "@/lib/hr";

function Kpi({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="rounded border border-[#CCCCCC] bg-white p-3 flex items-center gap-3">
      <span className="w-10 h-10 rounded grid place-items-center text-white" style={{ background: tone }}><Icon className="w-5 h-5" /></span>
      <div><div className="text-[11px] text-[#666]">{label}</div><div className="font-extrabold text-lg" data-testid={testid}>{value}</div></div>
    </div>
  );
}

function StaffTable({ onOpen, onPerms }) {
  const { view, t, money } = useApp();
  const month = today().slice(0, 7);
  const rows = view.staff.map((s) => {
    const evs = staffEvents(view.hr_events, s.id);
    return { s, debt: staffDebt(evs), net: monthSummary(s, evs, month).net, leave: onLeave(evs, today()) };
  });
  return (
    <div className="rounded border border-[#CCCCCC] bg-white overflow-x-auto" data-testid="staff-table">
      <table className="w-full text-sm">
        <thead><tr className="bg-[#E8E8E8] text-xs text-[#333]"><th className="text-start py-2 px-3">{t("name")}</th><th className="text-start px-3">{t("role")}</th><th className="text-start px-3">{t("hr_phone")}</th><th className="text-end px-3">{t("hr_salary")}</th><th className="text-end px-3">{t("hr_debt")}</th><th className="text-end px-3">{t("hr_net")}</th><th className="text-start px-3">{t("status")}</th><th className="px-3" /></tr></thead>
        <tbody>
          {rows.map(({ s, debt, net, leave }) => (
            <tr key={s.id} onClick={() => onOpen(s)} className="border-t border-[#E5E5E5] hover:bg-[#E5F1FB] cursor-pointer" data-testid="staff-row">
              <td className="py-2 px-3 font-bold">{s.name}</td>
              <td className="px-3">{t(`role_${s.role}`)}</td>
              <td className="px-3" dir="ltr">{s.phone || "—"}</td>
              <td className="px-3 text-end" dir="ltr">{s.salary ? money(s.salary) : "—"}</td>
              <td className={`px-3 text-end font-bold ${debt > 0 ? "text-[#E67E22]" : ""}`} dir="ltr">{money(debt)}</td>
              <td className="px-3 text-end" dir="ltr">{money(net)}</td>
              <td className="px-2 text-center">{s.role !== "owner" && <button type="button" onClick={(e) => { e.stopPropagation(); onPerms(s); }} title={t("perm_title")} className="w-9 h-9 rounded grid place-items-center hover:bg-[#E5F1FB] text-[#1F4E8C] relative" data-testid={`staff-perms-${s.name}`}><ShieldCheck className="w-5 h-5" />{(s.denied || []).length > 0 && <span className="absolute -top-0.5 -end-0.5 min-w-4 h-4 px-1 rounded-full bg-[#E74C3C] text-white text-[10px] grid place-items-center">{s.denied.length}</span>}</button>}</td>
              <td className="px-3">{!s.active ? <span className="text-[#888]">{t("inactive")}</span> : leave ? <span className="px-2 py-0.5 rounded bg-[#C7353B] text-white text-xs font-bold">{t(`hr_kind_${leave.kind}`)}</span> : <span className="px-2 py-0.5 rounded bg-[#E6F4EA] text-[#23714A] text-xs font-bold">{t("active")}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityList() {
  const { view, queue, t, lang } = useApp();
  const rows = [
    ...queue.map((o) => ({ id: o.op_id, type: o.type, staff_name: o.staff_name, ts: o.ts, pending: true })),
    ...view.activity,
  ];
  return (
    <div className="space-y-2" data-testid="activity-log-list">
      {rows.map((a) => (
        <div key={a.id} className="rounded-xl bg-white border border-border p-3 flex justify-between gap-2">
          <div><div className="font-bold">{t(`act_${a.type}`)} {a.summary && <span className="text-muted-foreground font-normal">· {a.summary}</span>}</div>
            <div className="text-xs text-muted-foreground">{a.staff_name} · {fmtDate(a.ts, lang)}</div></div>
          {a.pending && <span className="text-xs font-bold text-[#E67E22] shrink-0">{t("pending")}</span>}
        </div>
      ))}
    </div>
  );
}

function SyncIssues() {
  const { view, failed, clearFailed, t, lang } = useApp();
  const conflicts = vals(view.conflicts).sort((a, b) => b.created_at.localeCompare(a.created_at));
  return (
    <div className="space-y-2" data-testid="sync-issues-list">
      {failed.length > 0 && <BigButton variant="soft" onClick={clearFailed} className="w-full" data-testid="clear-failed-btn"><XCircle className="w-5 h-5" />{t("clear_failed")} ({failed.length})</BigButton>}
      {failed.map((f) => <div key={f.op_id} className="rounded-xl bg-destructive/10 p-3 text-sm" data-testid="failed-op-row"><b>{t(`act_${f.op?.type}`)}</b> · {syncErrText(lang, f.error)}{f.op?.ts && <span className="text-xs text-muted-foreground ms-2" dir="ltr">{fmtDate(f.op.ts, lang)}</span>}</div>)}
      {conflicts.length === 0 && !failed.length && <p className="text-muted-foreground">{t("no_conflicts")}</p>}
      {conflicts.map((c) => (
        <div key={c.id} className="rounded-xl bg-white border border-border p-3" data-testid="conflict-row">
          <div className="font-bold">{t(`conflict_${c.kind}`)} · {c.detail?.name}</div>
          <div className="text-sm text-muted-foreground">
            {c.kind === "price" ? t("conflict_price_detail", { server: c.detail.server_price, device: c.detail.device_price, kept: t(`kept_${c.detail.kept}`) }) : t("conflict_dup_detail", { name: c.detail?.existing_name })}
          </div>
          <div className="text-xs text-muted-foreground">{c.staff_name} · {fmtDate(c.created_at, lang)}</div>
        </div>
      ))}
    </div>
  );
}

export default function Team() {
  const { view, t, money } = useApp();
  const [editing, setEditing] = useState(null);
  const [perms, setPerms] = useState(null);
  const live = editing?.id ? view.staff.find((s) => s.id === editing.id) || editing : editing;
  const active = view.staff.filter((s) => s.active);
  const payroll = active.reduce((s, m) => s + (Number(m.salary || 0) * (m.salary_type === "daily" ? 26 : m.salary_type === "weekly" ? 4.33 : 1)), 0);
  const debt = view.staff.reduce((s, m) => s + staffDebt(staffEvents(view.hr_events, m.id)), 0);
  const leaveToday = active.filter((m) => onLeave(staffEvents(view.hr_events, m.id), today())).length;
  return (
    <div>
      <PageTitle testid="team-title" action={<BigButton onClick={() => setEditing({})} data-testid="staff-add-btn"><UserPlus className="w-5 h-5" />{t("add_staff")}</BigButton>}>{t("team")}</PageTitle>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
        <Kpi icon={Users} label={t("hr_active_staff")} value={active.length} tone="#2B79C2" testid="team-kpi-active" />
        <Kpi icon={Wallet} label={t("hr_total_payroll")} value={money(Math.round(payroll))} tone="#1F9D55" testid="team-kpi-payroll" />
        <Kpi icon={HandCoins} label={t("hr_total_debt")} value={money(debt)} tone="#E67E22" testid="team-kpi-debt" />
        <Kpi icon={UserX} label={t("hr_leave_today")} value={leaveToday} tone="#C7353B" testid="team-kpi-leave" />
        <Kpi icon={Timer} label={t("clocked_in_now")} value={Object.values(view.attendance || {}).filter((a) => !a.out_at).length} tone="#1F9D55" testid="team-kpi-clocked" />
      </div>
      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "staff"}>
        <TabsList className="w-full h-12 mb-4">
          <TabsTrigger value="staff" className="flex-1 h-10 font-bold" data-testid="team-tab-staff">{t("staff")}</TabsTrigger>
          <TabsTrigger value="activity" className="flex-1 h-10 font-bold" data-testid="team-tab-activity"><History className="w-4 h-4 me-1" />{t("activity")}</TabsTrigger>
          <TabsTrigger value="sync" className="flex-1 h-10 font-bold" data-testid="team-tab-sync"><ShieldAlert className="w-4 h-4 me-1" />{t("sync_issues")}</TabsTrigger>
        </TabsList>
        <TabsContent value="staff"><StaffTable onOpen={setEditing} onPerms={setPerms} /></TabsContent>
        <TabsContent value="activity"><ActivityList /></TabsContent>
        <TabsContent value="sync"><SyncIssues /></TabsContent>
      </Tabs>
      <StaffProfile member={live} onClose={() => setEditing(null)} />
      {perms && <PermissionsDialog member={view.staff[perms.id] || perms} onClose={() => setPerms(null)} />}
    </div>
  );
}
